import React, { useEffect, useRef, useState } from "react";
import { Globe, ChevronDown, Check } from "lucide-react";
import { useLang } from "@/i18n";
import { LANGUAGES } from "@/i18n/languages";

// Short label shown on the closed dropdown trigger. Falls back to the
// upper-cased base code (so a future activation like "ru" just works as
// "RU" without code changes here).
const SHORT_LABEL = { en: "EN", es: "ES", zh: "中文", "pt-br": "PT-BR" };
const shortLabel = (code) => SHORT_LABEL[code] || code.toUpperCase();

// Reusable language selector used in the public header and the admin
// sidebar. We expose two style variants and two test-id prefixes so the
// existing Playwright suites (which target `lang-{code}` and
// `admin-lang-{code}`) keep working unchanged — only the way the user
// reaches the option changes (button strip → dropdown menu).
const LanguageDropdown = ({ variant = "navbar", testIdPrefix = "lang" }) => {
  // Phase A18-B1 — the dropdown represents an explicit user choice, so
  // it calls `setLangManual` which marks the source as "manual" and
  // prevents subsequent automatic applies from AuthContext.login/refresh
  // from silently overwriting the choice mid-session.
  const { lang, setLangManual } = useLang();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Only currently active languages are offered — phase-4 registered-but-
  // not-yet-activated codes (only RU today) stay hidden.
  const active = LANGUAGES.filter((l) => l.isActive);
  const current = active.find((l) => l.code === lang) || active[0];

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const pick = (code) => {
    setLangManual(code);
    setOpen(false);
  };

  // Compact pill style for the navbar; full-width sidebar style for
  // the admin layout's narrow rail.
  const triggerCls =
    variant === "sidebar"
      ? "w-full flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition"
      : "flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition";

  const panelCls =
    variant === "sidebar"
      ? "absolute bottom-full mb-1 left-0 right-0 z-30 max-h-72 overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg shadow-slate-900/5 py-1"
      // MOBILE-FIX-01 — on small viewports the navbar dropdown panel
      // used to anchor to the trigger's right edge with `right-0`, which
      // on logged-out mobile views pushed the 11rem (176px) panel far
      // enough left that it visually OVERLAPPED the logo. We now pin
      // the panel to the viewport's right edge with `fixed top-16
      // right-2` on mobile (the public Navbar is sticky at h-16), and
      // restore the original trigger-anchored absolute layout from sm+.
      : "fixed top-16 end-2 sm:absolute sm:top-full sm:end-0 sm:mt-1 z-50 w-56 max-h-80 overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg shadow-slate-900/5 py-1";

  return (
    <div ref={wrapRef} className="relative" data-testid={`${testIdPrefix}-dropdown`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={triggerCls}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid={`${testIdPrefix}-dropdown-trigger`}
        title={current?.nameNative}
      >
        <Globe className="w-4 h-4 text-emerald-700 shrink-0" />
        <span className="truncate" data-testid={`${testIdPrefix}-current`}>
          {shortLabel(current?.code || "en")}
        </span>
        <ChevronDown className={`w-3.5 h-3.5 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <ul
          role="listbox"
          className={panelCls}
          data-testid={`${testIdPrefix}-dropdown-panel`}
        >
          {active.map((opt) => {
            const selected = opt.code === lang;
            return (
              <li key={opt.code} role="none">
                <button
                  role="option"
                  aria-selected={selected}
                  type="button"
                  onClick={() => pick(opt.code)}
                  className={`w-full flex items-center justify-between gap-3 px-3 py-2 text-sm text-start transition ${
                    selected ? "bg-emerald-50 text-emerald-700 font-semibold" : "text-slate-700 hover:bg-slate-50"
                  }`}
                  data-testid={`${testIdPrefix}-${opt.code}`}
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <span className="font-mono text-xs text-slate-400 w-12 shrink-0">
                      {shortLabel(opt.code)}
                    </span>
                    <span className="truncate">{opt.nameNative}</span>
                  </span>
                  {selected && <Check className="w-4 h-4 shrink-0 text-emerald-600" />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default LanguageDropdown;
